import { pipeline, env } from '@xenova/transformers';
// NOTE: @capacitor-community/speech-recognition is only for native mobile builds
// For web, speech recognition is handled by the Web Speech API in speechRecognition.ts
// import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { openDB } from 'idb';

// Configure transformers.js to load models from local assets or CDN
// For offline use, we would ideally bundle the model, but for now we'll use the default CDN
// which caches to the browser/webview cache.
env.allowLocalModels = false;
env.useBrowserCache = true;

interface VoiceCommand {
  id: string;
  phrase: string;
  action: string;
  params?: any;
  embedding?: number[];
  usageCount: number;
}

class VoiceCommandService {
  private dbPromise;
  private embedder: any = null;
  private isModelLoading = false;
  private commands: VoiceCommand[] = [];

  constructor() {
    this.dbPromise = openDB('OilWellVoice', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('commands')) {
          const store = db.createObjectStore('commands', { keyPath: 'id' });
          store.createIndex('phrase', 'phrase', { unique: true });
        }
      },
    });
  }

  /**
   * Initialize the embedding model
   * Uses a small, efficient model suitable for mobile devices
   */
  async initModel() {
    if (this.embedder || this.isModelLoading) return;
    
    try {
      this.isModelLoading = true;
      
      // 'Xenova/all-MiniLM-L6-v2' is a great balance of speed/accuracy for semantic search
      // It's about 20MB and runs fast in-browser
      this.embedder = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2');
      
      this.isModelLoading = false;
    } catch (error) {
      // In case of failure, ensure the service can still be used if the model loads later.
      this.embedder = null;
      console.error('Failed to load AI model:', error);
      this.isModelLoading = false;
    }
  }

  /**
   * Start listening for voice input
   * NOTE: This method uses Capacitor SpeechRecognition for native mobile.
   * For web builds, use the Web Speech API in speechRecognition.ts instead.
   */
  async startListening(): Promise<string> {
    // SpeechRecognition is only available in native mobile builds
    // For web, return empty string - web uses speechRecognition.ts instead
    if (typeof window !== 'undefined' && !(window as any).Capacitor?.isNativePlatform?.()) {
      console.warn('VoiceCommandService.startListening: Not in native mobile environment. Use speechRecognition.ts for web.');
      return '';
    }

    // Since Capacitor is not being used, return empty string for now
    console.warn('Native speech recognition not available without Capacitor setup');
    return '';
  }

  /**
   * Process a spoken phrase and find the best matching action
   */
  async processCommand(phrase: string): Promise<{ action: string; confidence: number; params?: any } | null> {
    if (!this.embedder) await this.initModel();
    if (!this.embedder) return null; // Model failed to load

    // 1. Generate embedding for the spoken phrase
    const output = await this.embedder(phrase, { pooling: 'mean', normalize: true });
    const vector = Array.from(output.data) as number[];

    // 2. If commands aren't cached, fetch them.
    if (this.commands.length === 0) {
      this.commands = await (await this.dbPromise).getAll('commands');
    }

    // 3. Find closest match (Cosine Similarity)
    let bestMatch: VoiceCommand | null = null;
    let maxSimilarity = -1;

    for (const cmd of this.commands) {
      if (!cmd.embedding) continue;
      
      const similarity = this.cosineSimilarity(vector, cmd.embedding);
      if (similarity > maxSimilarity) {
        maxSimilarity = similarity;
        bestMatch = cmd;
      }
    }

    // Threshold: If similarity is too low (< 0.7), we don't know this command
    if (maxSimilarity > 0.7 && bestMatch) {
      // Update usage count
      const db = await this.dbPromise;
      bestMatch.usageCount++;
      await db.put('commands', bestMatch);
      
      return {
        action: bestMatch.action,
        confidence: maxSimilarity,
        params: bestMatch.params
      };
    }

    return null; // Unknown command
  }

  /**
   * Teach the AI a new command
   */
  async learnCommand(phrase: string, action: string, params: any = {}) {
    if (!this.embedder) await this.initModel();
    if (!this.embedder) return; // Cannot learn without a model

    // Generate embedding
    const output = await this.embedder(phrase, { pooling: 'mean', normalize: true });
    const vector = Array.from(output.data) as number[];

    const command: VoiceCommand = {
      id: crypto.randomUUID(),
      phrase,
      action,
      params,
      embedding: vector,
      usageCount: 1
    };

    const db = await this.dbPromise;
    await db.put('commands', command);

    // Invalidate local cache to force a reload on next processCommand
    this.commands = [];
  }

  /**
   * Seed default commands if database is empty
   */
  async seedDefaults() {
    const db = await this.dbPromise;
    const count = await db.count('commands');
    if (count > 0) return;

    const defaults = [
      { phrase: "Show me the wells", action: "NAVIGATE_WELLS", params: {} },
      { phrase: "Go to dashboard", action: "NAVIGATE_DASHBOARD", params: {} },
      { phrase: "Open map", action: "NAVIGATE_MAP", params: {} },
      { phrase: "Show analytics", action: "NAVIGATE_ANALYTICS", params: {} },
      { phrase: "Create ticket", action: "CREATE_TICKET", params: {} },
      { phrase: "Start data entry", action: "START_DATA_ENTRY", params: {} },
      { phrase: "Show alerts", action: "SHOW_ALERTS", params: {} },
      { phrase: "My schedule", action: "SHOW_SCHEDULE", params: {} },
      { phrase: "Sync data", action: "SYNC_DATA", params: {} },
      { phrase: "Log out", action: "LOGOUT", params: {} },
    ];

    for (const def of defaults) {
      await this.learnCommand(def.phrase, def.action, def.params);
    }
  }

  /**
   * Generate embedding vector for any text (public method for ML training)
   */
  async generateEmbedding(text: string): Promise<Float32Array | null> {
    if (!this.embedder) await this.initModel();
    if (!this.embedder) return null;

    try {
      const output = await this.embedder(text, { pooling: 'mean', normalize: true });
      return new Float32Array(output.data);
    } catch (error) {
      console.error('Failed to generate embedding:', error);
      return null;
    }
  }

  // --- Math Helper ---
  private cosineSimilarity(a: number[], b: number[]): number {
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }
    return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
  }
}

export const voiceCommandService = new VoiceCommandService();
